import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './client';

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-run the GET (e.g. after a mutation elsewhere). */
  refetch: () => void;
}

/**
 * Minimal GET hook for read-only screens and dashboard widgets. Pass `null`
 * to skip the fetch (e.g. while a prerequisite isn't ready). Mirrors the
 * loading/error pattern the screens already hand-roll, centralised so the
 * dashboard widgets stay terse.
 */
export function useApi<T>(path: string | null): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(path != null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path == null) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    apiFetch<T>(path)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [path, nonce]);

  return { data, loading, error, refetch };
}
